import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { PushDrawer, PushDrawerProps } from '../components/PushDrawer';
import { SettingsDrawer } from '../components/settings-drawer/SettingsDrawer';
import { logger } from '../datadog/logger';
import { useDrawerButtonRefs } from '../hooks/useDrawerButtonRefs';
import { currentConversationSidAtom } from '../session';
import { isVirtualBackgroundSupportedAtom } from '../video-sdk/core/atoms';
import { AllClientsChatDrawerWrapper as AllClientsChatDrawer } from './chat/AllClientsChatDrawerWrapper';
import { ChatDrawerWrapper as ChatDrawer } from './chat/ChatDrawerWrapper';
import { ClientInformationDrawerWrapper as ClientInformationDrawer } from './client-information/ClientInformationDrawerWrapper';
import { ClientInformationFormResponseDrawerWrapper as ClientInformationFormResponseDrawer } from './client-information/ClientInformationFormResponseDrawerWrapper';
import { ClientInformationProgressNotesDrawerWrapper as ClientInformationNotesDrawer } from './client-information/ClientInformationProgressNotesDrawerWrapper';
import { PendingFormsCompletedDrawer } from './client-information/pending-forms/PendingFormsCompletedDrawer';
import { PendingFormsDrawer } from './client-information/pending-forms/PendingFormsDrawer';
import { TreatmentPlansContentDrawerWrapper as TreatmentPlansContentDrawer } from './client-information/treatment-tab/TreatmentPlansContentWrapper';
import { ClientsDrawerWrapper as ClientsDrawer } from './clients-drawer/ClientsDrawerWrapper';
import { CSSRSFormWrapper as CSSRSForm } from './measures/CSSRSFormWrapper';
import { MeasureResponsesDrawer } from './measures/MeasureResponsesDrawer';
import { PendingMeasuresDrawerWrapper as PendingMeasuresDrawer } from './measures/PendingMeasuresDrawerWrapper';
import {
  type MeasureResponsesTrackingState,
  createMeasureResponsesTrackingState,
} from './measures/useTrackMeasureResponsesDrawerView';
import { SchedulingAppointmentDrawerWrapper } from './schedule-appointment/ScheduleAppointmentDrawerWrapper';
import { getSupportedTabs } from './settingsTabMap';
import {
  ProviderDrawerState,
  drawerStateAtom,
  measureResponsesEntryContextAtom,
  selectedBundleAtom,
  selectedMeasureBundleItemIdAtom,
} from './state';

function DrawerContent({
  drawerState,
  isVirtualBackgroundSupported,
  measureResponsesTrackingState,
}: {
  drawerState: ProviderDrawerState | null;
  isVirtualBackgroundSupported?: boolean;
  measureResponsesTrackingState: MutableRefObject<MeasureResponsesTrackingState>;
}) {
  const supportedTabs = useMemo(
    () => getSupportedTabs({ isVirtualBackgroundSupported }),
    [isVirtualBackgroundSupported],
  );

  switch (drawerState) {
    case ProviderDrawerState.SETTINGS:
      return <SettingsDrawer tabMap={supportedTabs} />;
    case ProviderDrawerState.CLIENTS:
      return <ClientsDrawer />;
    case ProviderDrawerState.SESSION_CHAT:
    case ProviderDrawerState.WAITING_ROOM_CHAT:
    case ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT:
      return <ChatDrawer />;
    case ProviderDrawerState.ALL_CLIENTS_CHAT_LIST:
      return <AllClientsChatDrawer />;
    case ProviderDrawerState.CLIENT_INFORMATION:
      return <ClientInformationDrawer />;
    case ProviderDrawerState.CLIENT_FORM_RESPONSE:
      return <ClientInformationFormResponseDrawer />;
    case ProviderDrawerState.TREATMENT_PLANS_CONTENT:
      return <TreatmentPlansContentDrawer />;
    case ProviderDrawerState.PROGRESS_NOTES:
      return <ClientInformationNotesDrawer />;
    case ProviderDrawerState.EDIT_APPOINTMENT:
      return <SchedulingAppointmentDrawerWrapper is_add_appointment={false} />;
    case ProviderDrawerState.PENDING_FORMS:
      return <PendingFormsDrawer />;
    case ProviderDrawerState.PENDING_FORMS_COMPLETED:
      return <PendingFormsCompletedDrawer />;
    case ProviderDrawerState.ADD_APPOINTMENT:
      return <SchedulingAppointmentDrawerWrapper is_add_appointment={true} />;
    case ProviderDrawerState.PENDING_MEASURES:
      return <PendingMeasuresDrawer />;
    case ProviderDrawerState.MEASURE_RESPONSES:
      return <MeasureResponsesDrawer trackingState={measureResponsesTrackingState} />;
    case ProviderDrawerState.CSSRS_FORM:
      return <CSSRSForm />;
    default:
      return null;
  }
}

function logPushDrawerError(error: Error) {
  logger.error('The push drawer crashed after encountering an unexpected error; displaying fallback', error);
}

export function PushDrawerWrapper({ className, id }: Pick<PushDrawerProps, 'className' | 'id'>) {
  const { buttonRefs: openButtonRefs } = useDrawerButtonRefs();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
  const [currentConversationSid, setCurrentConversationSid] = useAtom(currentConversationSidAtom);
  const setMeasureResponsesEntryContext = useSetAtom(measureResponsesEntryContextAtom);
  const setSelectedMeasureBundle = useSetAtom(selectedBundleAtom);
  const setSelectedMeasureBundleItem = useSetAtom(selectedMeasureBundleItemIdAtom);
  const isVirtualBackgroundSupported = useAtomValue(isVirtualBackgroundSupportedAtom);
  const measureResponsesTrackingState = useRef(createMeasureResponsesTrackingState());

  const onCloseDrawer = useCallback((): void => {
    setDrawerState(null);
    setSelectedMeasureBundle(undefined);
    setSelectedMeasureBundleItem(undefined);
    if (currentConversationSid) {
      setCurrentConversationSid(undefined);
    }
    if (drawerState) {
      // On drawer close, try to refocus the button that would normally open
      // whatever `drawerState` was active at the time of closing.
      openButtonRefs.current?.get(drawerState)?.current?.focus();
    }
  }, [
    currentConversationSid,
    drawerState,
    openButtonRefs,
    setCurrentConversationSid,
    setDrawerState,
    setSelectedMeasureBundle,
    setSelectedMeasureBundleItem,
  ]);

  // On drawer state change, refocus the "close" button
  useEffect(
    function focusCloseButtonOnDrawerChange() {
      if (drawerState) {
        closeButtonRef.current?.focus();
      }
    },
    [drawerState],
  );

  useEffect(
    function clearMeasureResponsesTrackingWhenLeavingFlow() {
      if (!drawerState) {
        setMeasureResponsesEntryContext(undefined);
      }
      if (drawerState !== ProviderDrawerState.MEASURE_RESPONSES && drawerState !== ProviderDrawerState.CSSRS_FORM) {
        measureResponsesTrackingState.current = createMeasureResponsesTrackingState();
      }
    },
    [drawerState, setMeasureResponsesEntryContext],
  );

  return (
    <PushDrawer
      onError={logPushDrawerError}
      id={id}
      isOpen={!!drawerState}
      onClose={onCloseDrawer}
      className={className}
      closeButtonRef={closeButtonRef}
    >
      <DrawerContent
        drawerState={drawerState}
        isVirtualBackgroundSupported={isVirtualBackgroundSupported}
        measureResponsesTrackingState={measureResponsesTrackingState}
      />
    </PushDrawer>
  );
}
